import { CompanyTypeDto } from '@taxora-api-models';

export enum OrganizationLegalForm {
  AFJ = 'AFJ',
  ASF = 'ASF',
  CON = 'CON',
  CRL = 'CRL',
  INC = 'INC',
  LOC = 'LOC',
  OC1 = 'OC1',
  OC2 = 'OC2',
  OC3 = 'OC3',
  SRL = 'SRL',
  SA = 'SA',
  SCS = 'SCS',
  SNC = 'SNC',
  SPI = 'SPI',
  RA = 'RA',
  URL = 'URL',
  PFA = 'PFA',
  II = 'II',
  IF = 'IF',
}

const OFFICIAL_ANAF_ORGANIZATION_LEGAL_FORMS: Array<{ value: string; label: string }> = [
  { value: OrganizationLegalForm.AFJ, label: 'Alte forme juridice' },
  { value: OrganizationLegalForm.ASF, label: 'Asociatie familiala' },
  { value: OrganizationLegalForm.CON, label: 'Concesiune' },
  {
    value: OrganizationLegalForm.CRL,
    label: 'Societate civila profesionala cu personalitate juridica si raspundere limitata',
  },
  { value: OrganizationLegalForm.INC, label: 'Inchiriere' },
  { value: OrganizationLegalForm.LOC, label: 'Locatie de gestiune' },
  { value: OrganizationLegalForm.OC1, label: 'Organizatie cooperatista mestesugareasca' },
  { value: OrganizationLegalForm.OC2, label: 'Organizatie cooperatista de consum' },
  { value: OrganizationLegalForm.OC3, label: 'Organizatie cooperatista de credit' },
  { value: OrganizationLegalForm.PFA, label: 'Persoana fizica independenta' },
  { value: OrganizationLegalForm.RA, label: 'Regie autonoma' },
  { value: OrganizationLegalForm.SA, label: 'Societate comerciala pe actiuni' },
  { value: OrganizationLegalForm.SCS, label: 'Societate comerciala in comandita simpla' },
  { value: OrganizationLegalForm.SNC, label: 'Societate comerciala in nume colectiv' },
  {
    value: OrganizationLegalForm.SPI,
    label: 'Societate profesionala practicieni in insolventa',
  },
  { value: OrganizationLegalForm.SRL, label: 'Societate comerciala cu raspundere limitata' },
  {
    value: OrganizationLegalForm.URL,
    label: 'Intreprindere profesionala unipersonala cu raspundere limitata',
  },
  { value: OrganizationLegalForm.II, label: 'Intreprindere individuala' },
  { value: OrganizationLegalForm.IF, label: 'Intreprindere familiala' },
];

const ANAF_ORGANIZATION_LEGAL_FORM_ALIASES: Record<string, string[]> = {
  [OrganizationLegalForm.AFJ]: ['ALTE FORME JURIDICE', 'AFJ'],
  [OrganizationLegalForm.ASF]: ['ASOCIATIE FAMILIALA', 'ASF'],
  [OrganizationLegalForm.CON]: ['CONCESIUNE', 'CON'],
  [OrganizationLegalForm.CRL]: [
    'SOCIETATE CIVILA PROFESIONALA CU PERSONALITATE JURIDICA SI RASPUNDERE LIMITATA',
    'SOCIETATE CIVILA PROFESIONALA CU PERS JURIDICA SI RASPUNDERE LIMITATA',
    'SPRL',
    'CRL',
  ],
  [OrganizationLegalForm.INC]: ['INCHIRIERE', 'INC'],
  [OrganizationLegalForm.LOC]: ['LOCATIE DE GESTIUNE', 'LOC'],
  [OrganizationLegalForm.OC1]: ['ORGANIZATIE COOPERATISTA MESTESUGAREASCA', 'OC1'],
  [OrganizationLegalForm.OC2]: ['ORGANIZATIE COOPERATISTA DE CONSUM', 'OC2'],
  [OrganizationLegalForm.OC3]: ['ORGANIZATIE COOPERATISTA DE CREDIT', 'OC3'],
  [OrganizationLegalForm.SRL]: [
    'SOCIETATE COMERCIALA CU RASPUNDERE LIMITATA',
    'SOCIETATE CU RASPUNDERE LIMITATA',
    'SRL',
    'S.R.L.',
  ],
  [OrganizationLegalForm.SA]: [
    'SOCIETATE COMERCIALA PE ACTIUNI',
    'SOCIETATE PE ACTIUNI',
    'SA',
    'S.A.',
  ],
  [OrganizationLegalForm.SCS]: [
    'SOCIETATE COMERCIALA IN COMANDITA SIMPLA',
    'SOCIETATE IN COMANDITA SIMPLA',
    'SCS',
    'S.C.S.',
  ],
  [OrganizationLegalForm.SNC]: [
    'SOCIETATE COMERCIALA IN NUME COLECTIV',
    'SOCIETATE IN NUME COLECTIV',
    'SNC',
    'S.N.C.',
  ],
  [OrganizationLegalForm.SPI]: ['SOCIETATE PROFESIONALA PRACTICIENI IN INSOLVENTA', 'SPPI', 'SPI'],
  [OrganizationLegalForm.RA]: ['REGIE AUTONOMA', 'RA'],
  [OrganizationLegalForm.URL]: [
    'INTREPRINDERE PROFESIONALA UNIPERSONALA CU RASPUNDERE LIMITATA',
    'IPURL',
    'URL',
  ],
  [OrganizationLegalForm.PFA]: [
    'PERSOANA FIZICA AUTORIZATA',
    'PERSOANA FIZICA INDEPENDENTA',
    'PFA',
    'P.F.A.',
  ],
  [OrganizationLegalForm.II]: ['INTREPRINDERE INDIVIDUALA', 'II', 'I.I.'],
  [OrganizationLegalForm.IF]: ['INTREPRINDERE FAMILIALA', 'IF', 'I.F.'],
};

export function getOrganizationLegalFormValue(
  legalForm: string | null | undefined,
  companyTypes: CompanyTypeDto[],
): string {
  const normalizedLegalForm = normalizeLegalForm(legalForm);

  if (!normalizedLegalForm) {
    return '';
  }

  const exactCompanyTypeMatch = companyTypes.find(
    (companyType: CompanyTypeDto) =>
      normalizeLegalForm(companyType.value) === normalizedLegalForm ||
      normalizeLegalForm(companyType.label) === normalizedLegalForm,
  );

  if (exactCompanyTypeMatch) {
    return exactCompanyTypeMatch.value;
  }

  for (const [value, aliases] of Object.entries(ANAF_ORGANIZATION_LEGAL_FORM_ALIASES)) {
    if (aliases.some((alias: string) => normalizeLegalForm(alias) === normalizedLegalForm)) {
      return findCompanyTypeValue(companyTypes, value) ?? value;
    }
  }

  const partialCompanyTypeMatch = companyTypes.find((companyType: CompanyTypeDto) => {
    const normalizedValue = normalizeLegalForm(companyType.value);
    const normalizedLabel = normalizeLegalForm(companyType.label);

    return (
      normalizedLegalForm.includes(normalizedValue) ||
      normalizedValue.includes(normalizedLegalForm) ||
      normalizedLegalForm.includes(normalizedLabel) ||
      normalizedLabel.includes(normalizedLegalForm)
    );
  });

  if (partialCompanyTypeMatch) {
    return partialCompanyTypeMatch.value;
  }

  for (const [value, aliases] of Object.entries(ANAF_ORGANIZATION_LEGAL_FORM_ALIASES)) {
    if (
      aliases.some((alias: string) => {
        const normalizedAlias = normalizeLegalForm(alias);

        return (
          normalizedLegalForm.includes(normalizedAlias) ||
          normalizedAlias.includes(normalizedLegalForm)
        );
      })
    ) {
      return findCompanyTypeValue(companyTypes, value) ?? value;
    }
  }

  return '';
}

export function mapCompanyTypesToOrganizationLegalFormOptions(
  companyTypes: CompanyTypeDto[],
): Array<{ value: string; label: string }> {
  const options = new Map<string, { value: string; label: string }>();

  for (const companyType of companyTypes) {
    options.set(companyType.value, {
      value: companyType.value,
      label: companyType.label,
    });
  }

  for (const option of OFFICIAL_ANAF_ORGANIZATION_LEGAL_FORMS) {
    if (!options.has(option.value)) {
      options.set(option.value, option);
    }
  }

  return Array.from(options.values());
}

function findCompanyTypeValue(companyTypes: CompanyTypeDto[], value: string): string | undefined {
  return companyTypes.find(
    (companyType: CompanyTypeDto) =>
      normalizeLegalForm(companyType.value) === normalizeLegalForm(value),
  )?.value;
}

function normalizeLegalForm(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9 ]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}
